import React from 'react';
import { useFormContext } from 'react-hook-form';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Popover from '@mui/material/Popover';
import classNames from 'classnames';
import { kebabCase } from 'lodash';
import ControlledSwitch from '@/components/common/form/ControlledSwitch';
import type ConstituentSearchColumn from '@/constants/ConstituentSearchColumn';
import translate from '@/lib/intl/translate';
import type CustomField from '@/types/api/CustomField';
import ColumnsLabelMap from '../constants/ColumnsLabelMap';

type ConstituentColumnSelectSubMenuProps = {
  label: string;
  columns?: ConstituentSearchColumn[];
  customFields?: CustomField[];
};

function ConstituentColumnSelectSubMenu({
  label,
  columns,
  customFields,
}: ConstituentColumnSelectSubMenuProps) {
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const { control } = useFormContext();

  const hasColumns = Boolean(columns?.length);
  const hasCustomFields = Boolean(customFields?.length);

  if (!hasColumns && !hasCustomFields) {
    return null;
  }

  return (
    <>
      <Button
        className={classNames(
          { 'bg-grey-100': isOpen },
          'justify-between rounded-none px-3 font-normal text-text-primary hover:bg-grey-100',
        )}
        ref={buttonRef}
        onClick={() => setIsOpen(true)}
        endIcon={<NavigateNextIcon />}
        data-testid={`columns-submenu-button-${kebabCase(label)}`}
      >
        {label}
      </Button>
      <Popover
        open={isOpen}
        anchorEl={buttonRef.current}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={() => setIsOpen(false)}
      >
        <div className="max-h-96 w-72 overflow-y-scroll p-3">
          <div className="grid grid-cols-1 gap-3">
            {hasColumns &&
              columns?.map((id) => (
                <FormControlLabel
                  key={id}
                  control={
                    <ControlledSwitch
                      control={control}
                      name={id}
                      data-testid={`columns-switch-${id}`}
                    />
                  }
                  label={translate(ColumnsLabelMap[id])}
                />
              ))}
            {hasCustomFields &&
              customFields?.map(({ fieldName, customFieldId }) => (
                <FormControlLabel
                  key={customFieldId}
                  control={
                    <ControlledSwitch
                      control={control}
                      name={customFieldId}
                      data-testid={`columns-switch-${customFieldId}`}
                    />
                  }
                  label={fieldName}
                />
              ))}
          </div>
        </div>
      </Popover>
    </>
  );
}

export default ConstituentColumnSelectSubMenu;
